import React, { useState, useEffect } from 'react';
import {
  X, Package, DollarSign, Tag, TrendingUp, Eye, MousePointer,
  ShoppingCart, BarChart3, Star, ExternalLink, Megaphone, MessageCircle, FileText, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { cn } from '../lib/utils';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const marketplaceConfig = {
  amazon: { name: 'Amazon', color: '#FF9900' },
  mercadolibre: { name: 'MercadoLibre', color: '#FFE600' },
  shopify: { name: 'Shopify', color: '#96BF48' },
  ebay: { name: 'eBay', color: '#E53238' },
  etsy: { name: 'Etsy', color: '#F1641E' },
  woocommerce: { name: 'WooCommerce', color: '#96588A' },
  other: { name: 'Otro', color: '#6B7280' }
};

export const ProductProfile = ({ product, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!product?.id) return;
    const fetchStats = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/products/${product.id}/stats`);
        setStats(res.data);
      } catch (err) {
        // Error handled by state
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [product?.id]);

  if (!product) return null;

  const margin = product.price && product.cost
    ? ((product.price - product.cost) / product.price * 100).toFixed(1)
    : null;
  const mpConfig = product.marketplace_source ? marketplaceConfig[product.marketplace_source] : null;

  return (
    <div
      data-testid="product-profile-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className={cn(
          "w-full max-w-3xl max-h-[90vh] overflow-hidden animate-scale-in",
          "bg-primary/95",
          "backdrop-blur-2xl rounded-3xl",
          "border border-white/20",
          "shadow-[0_25px_80px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.1)]"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="h-20 px-6 flex items-center justify-between bg-primary">
          <div className="flex items-center gap-4">
            {product.images?.[0] ? (
              <img src={product.images[0]} alt={product.name} className="w-14 h-14 rounded-xl object-cover ring-2 ring-white/40 shadow-lg" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center ring-2 ring-white/40">
                <Package className="w-7 h-7 text-white" />
              </div>
            )}
            <div className="text-white">
              <h2 className="text-xl font-bold drop-shadow-md">{product.name}</h2>
              <div className="flex items-center gap-2 text-white/80 text-sm">
                {product.category && <Badge className="bg-white/20 text-white border-none text-xs">{product.category}</Badge>}
                {mpConfig && <Badge className="text-white border-none text-xs" style={{ backgroundColor: `${mpConfig.color}40` }}>{mpConfig.name}</Badge>}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
            <X className="w-6 h-6" />
          </Button>
        </div>

        <ScrollArea className="max-h-[calc(90vh-5rem)]">
          <div className="p-6 space-y-6">
            {/* Price & Margin Banner */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatBox icon={DollarSign} label="Precio" value={product.price ? `$${product.price}` : 'N/A'} color="text-emerald-700 dark:text-emerald-400" />
              <StatBox icon={Tag} label="Costo" value={product.cost ? `$${product.cost}` : 'N/A'} color="text-brand-deep dark:text-brand-green" />
              <StatBox icon={TrendingUp} label="Margen" value={margin ? `${margin}%` : 'N/A'} color="text-amber-700 dark:text-amber-400" />
              <StatBox icon={ShoppingCart} label="Stock" value={product.stock != null ? product.stock : 'N/A'} color="text-brand-deep dark:text-brand-deep dark:text-brand-green" />
            </div>

            {/* Description */}
            {product.description && (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">{product.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Analytics Stats */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : stats && (
              <>
                {/* Performance Metrics */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                    Rendimiento
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <MetricCard icon={Eye} label="Vistas" value={stats.views?.toLocaleString()} trend="+12%" />
                    <MetricCard icon={MousePointer} label="Clicks" value={stats.clicks?.toLocaleString()} trend="+8%" />
                    <MetricCard icon={ShoppingCart} label="Conversiones" value={stats.conversions?.toLocaleString()} trend="+5%" />
                    <MetricCard icon={TrendingUp} label="Tasa Conv." value={`${stats.conversion_rate}%`} trend="+2.1%" />
                  </div>
                </div>

                {/* Revenue & Engagement */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Card className="bg-primary/10 border-emerald-500/20">
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">Ingresos Generados</p>
                      <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">${stats.revenue?.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-primary/10 border-violet-500/20">
                    <CardContent className="p-4 flex gap-6">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Cotizaciones</p>
                        <p className="text-2xl font-bold text-brand-deep dark:text-brand-deep dark:text-brand-green">{stats.quotes_generated}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Menciones en Chat</p>
                        <p className="text-2xl font-bold text-fuchsia-700 dark:text-fuchsia-300">{stats.chat_mentions}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Rating */}
                {stats.avg_rating && (
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-amber-700 dark:text-amber-400 fill-amber-400" />
                        <span className="text-lg font-bold">{stats.avg_rating}</span>
                        <span className="text-sm text-muted-foreground">({stats.reviews_count} resenas)</span>
                      </div>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(i => (
                          <Star key={`star-${i}`} className={cn("w-4 h-4", i <= Math.round(stats.avg_rating) ? "text-amber-700 dark:text-amber-400 fill-amber-400" : "text-muted-foreground/30")} />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Published channels */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Megaphone className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                    Publicado en
                  </h3>
                  <div className="space-y-2">
                    {mpConfig && (
                      <Card className="bg-white/5 border-white/10">
                        <CardContent className="p-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${mpConfig.color}20` }}>
                              <ShoppingCart className="w-4 h-4" style={{ color: mpConfig.color }} />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{mpConfig.name}</p>
                              {product.marketplace_sku && <p className="text-xs text-muted-foreground">SKU: {product.marketplace_sku}</p>}
                            </div>
                          </div>
                          {product.marketplace_url && (
                            <a href={product.marketplace_url} target="_blank" rel="noopener noreferrer">
                              <Button variant="outline" size="sm" className="gap-1 text-xs">
                                <ExternalLink className="w-3 h-3" /> Ver
                              </Button>
                            </a>
                          )}
                        </CardContent>
                      </Card>
                    )}
                    {stats.campaigns_linked?.map((camp) => (
                      <Card key={camp.id} className="bg-white/5 border-white/10">
                        <CardContent className="p-3 flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                            <Megaphone className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{camp.name}</p>
                            <p className="text-xs text-muted-foreground">Campana vinculada</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {!mpConfig && (!stats.campaigns_linked || stats.campaigns_linked.length === 0) && (
                      <p className="text-sm text-muted-foreground text-center py-4">No hay canales de publicacion configurados</p>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Images Gallery */}
            {product.images?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3">Galeria</h3>
                <div className="grid grid-cols-3 gap-2">
                  {product.images.map((img, idx) => (
                    <img key={`ref-img-${idx}`} src={img} alt="" className="w-full h-24 object-cover rounded-lg border border-white/10" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

const StatBox = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-white/5 border-white/10">
    <CardContent className="p-3 flex items-center gap-3">
      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center bg-current/10", color)}>
        <Icon className={cn("w-4 h-4", color)} />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-bold">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const MetricCard = ({ icon: Icon, label, value, trend }) => (
  <Card className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors">
    <CardContent className="p-3">
      <div className="flex items-center justify-between mb-1">
        <Icon className="w-4 h-4 text-muted-foreground" />
        {trend && <span className="text-xs text-emerald-700 dark:text-emerald-400">{trend}</span>}
      </div>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </CardContent>
  </Card>
);
